import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../hooks/useChat';
import { fetchBusinessContext, calculateMetrics } from '../../services/aiChatbotService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import toast from 'react-hot-toast';

const AIChatbot = () => {
  const { userProfile } = useAuth();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const messagesEndRef = useRef(null);
  
  const { response, isLoading, error, sendMessage } = useChat(
    'OPEN_AI',
    'gpt-5',
    true
  );

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'Failed to get AI response');
    }
  }, [error]);

  useEffect(() => {
    loadBusinessMetrics();
  }, []);

  useEffect(() => {
    if (response && !isLoading) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: response, timestamp: new Date()?.toISOString() }
      ]);
    }
  }, [response, isLoading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, response]);

  const loadBusinessMetrics = async () => {
    try {
      setLoadingMetrics(true);
      const data = await fetchBusinessContext();
      const calculatedMetrics = calculateMetrics(data);
      setMetrics(calculatedMetrics);
    } catch (err) {
      console.error('Load metrics error:', err);
      toast?.error('Failed to load business metrics');
    } finally {
      setLoadingMetrics(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const buildSystemPrompt = () => {
    if (!metrics) return 'You are an AI assistant for PropquesOS.';

    return `You are an AI assistant for PropquesOS, a commercial real estate management platform. You help admins and managers analyze their business data and provide actionable insights.

Current Business Metrics:
- Occupancy: ${metrics?.occupancy?.occupiedUnits}/${metrics?.occupancy?.totalUnits} units (${metrics?.occupancy?.occupancyRate}% occupancy rate)
- Vacant Units: ${metrics?.occupancy?.vacantUnits}
- Active Tenants: ${metrics?.tenants?.activeTenants}
- Expiring Leases (Next 3 Months): ${metrics?.tenants?.expiringLeases}
- Total Revenue: $${metrics?.revenue?.totalRevenue?.toLocaleString()}
- Avg Rent per Sqft: $${metrics?.revenue?.avgRentPerSqft}
- Total Leads: ${metrics?.leads?.totalLeads}
- Lead Conversion Rate: ${metrics?.leads?.conversionRate}%
- Lead Pipeline: New (${metrics?.leads?.leadsByStage?.new}), Contacted (${metrics?.leads?.leadsByStage?.contacted}), Qualified (${metrics?.leads?.leadsByStage?.qualified}), Proposal (${metrics?.leads?.leadsByStage?.proposal}), Negotiation (${metrics?.leads?.leadsByStage?.negotiation}), Won (${metrics?.leads?.leadsByStage?.won}), Lost (${metrics?.leads?.leadsByStage?.lost})
- Total Centers: ${metrics?.centers?.totalCenters}
- Total Sqft: ${metrics?.centers?.totalSqft?.toLocaleString()}

Your role:
1. Answer questions about occupancy, revenue, tenants, leads, and business performance
2. Provide data-driven recommendations and insights
3. Identify trends, risks, and opportunities
4. Suggest actionable next steps
5. Be concise, professional, and data-focused

When asked about specific data, reference the actual numbers provided above. Provide context and analysis, not just raw numbers.`;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!input?.trim() || isLoading) return;

    const userMessage = { 
      role: 'user', 
      content: input, 
      timestamp: new Date()?.toISOString() 
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    const conversationMessages = [
      { role: 'system', content: buildSystemPrompt() },
      ...messages?.map(msg => ({ role: msg?.role, content: msg?.content })),
      { role: 'user', content: input }
    ];

    sendMessage(conversationMessages, { max_completion_tokens: 1500 });
  };

  const handleClearChat = () => {
    setMessages([]);
    toast?.success('Chat history cleared');
  };

  const handleRefreshData = async () => {
    await loadBusinessMetrics();
    toast?.success('Business data refreshed');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loadingMetrics) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading AI Chatbot...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2">AI Business Assistant</h1>
        <p className="text-muted-foreground">
          Ask questions about occupancy, revenue, tenants, leads, and get AI-powered insights
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Data Summary Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Business Data</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRefreshData}
                  disabled={loadingMetrics}
                >
                  <Icon name="RefreshCw" size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Occupancy */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Building2" size={16} className="text-primary" />
                    <span className="text-sm font-semibold">Occupancy</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.occupancy?.occupancyRate}%
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {metrics?.occupancy?.occupiedUnits}/{metrics?.occupancy?.totalUnits} units
                  </div>
                </div>

                {/* Revenue */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="DollarSign" size={16} className="text-success" />
                    <span className="text-sm font-semibold">Revenue</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    ${metrics?.revenue?.totalRevenue?.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    ${metrics?.revenue?.avgRentPerSqft}/sqft avg
                  </div>
                </div>

                {/* Tenants */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Users" size={16} className="text-info" />
                    <span className="text-sm font-semibold">Tenants</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.tenants?.activeTenants}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {metrics?.tenants?.expiringLeases} expiring soon
                  </div>
                </div>

                {/* Leads */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Target" size={16} className="text-warning" />
                    <span className="text-sm font-semibold">Leads</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.leads?.totalLeads}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {metrics?.leads?.conversionRate}% conversion
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chat Interface */}
        <div className="lg:col-span-3">
          <Card className="h-[calc(100vh-200px)] flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Chat with AI Assistant</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearChat}
                  disabled={messages?.length === 0}
                >
                  <Icon name="Trash2" size={16} className="mr-2" />
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col overflow-hidden">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto mb-4 space-y-4">
                {messages?.length === 0 && (
                  <div className="text-center py-12">
                    <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">No messages yet</p>
                    <p className="text-sm text-muted-foreground">
                      Ask me anything about your business metrics, occupancy, revenue, or leads
                    </p>
                  </div>
                )}

                {messages?.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${
                      msg?.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {msg?.role === 'assistant' && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <Icon name="Bot" size={16} className="text-primary-foreground" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg?.role === 'user' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg?.content}</p>
                      <p className="text-xs mt-1 opacity-70">
                        {formatTimestamp(msg?.timestamp)}
                      </p>
                    </div>
                    {msg?.role === 'user' && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                        <Icon name="User" size={16} className="text-secondary-foreground" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Streaming response */}
                {isLoading && response && (
                  <div className="flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <Icon name="Bot" size={16} className="text-primary-foreground" />
                    </div>
                    <div className="max-w-[80%] rounded-lg p-3 bg-muted text-foreground">
                      <p className="text-sm whitespace-pre-wrap">{response}</p>
                      <div className="flex items-center gap-1 mt-2">
                        <Icon name="Loader2" size={12} className="animate-spin" />
                        <span className="text-xs opacity-70">Typing...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Form */}
              <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e?.target?.value)}
                  placeholder="Ask about occupancy, revenue, tenants, leads..."
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  disabled={isLoading || !input?.trim()}
                  iconName={isLoading ? 'Loader2' : 'Send'}
                >
                  {isLoading ? 'Sending...' : 'Send'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIChatbot;
